<template>
	<div class="pb-4 border-b last:border-b-0 last:pb-0">
		<div class="flex w-full justify-center space-x-2 px-2">
			<div class="flex flex-col justify-center w-[80px] h-[80px] shrink-0">
				<img :src="item.variant?.imageUrl || item.item.images?.find((image) => image.isThumbnail)?.url" class="max-w-[80px] max-h-[80px] object-contain rounded-lg" />
			</div>
			<div class="w-full">
				<div class="flex w-full justify-between">
					<div class="text-ihh-midnight-gray w-full max-w-96 mr-4">
						<router-link @click.prevent="handleProductClick" :to="`/products${item.item.customUrl.url}`" class="font-semibold text-base">{{
							item.item.name
						}}</router-link>
						<div class="text-xs">{{ item.item.brand?.name }}</div>
					</div>
					<!-- <div class="text-rum-500 text-sm">${{ item.variant?.calculatedPrice?.toFixed?.(2) || item.item?.calculatedPrice?.toFixed(2) }}</div> -->
					<div class="text-rum-500 text-sm">
						<span :class="{ 'line-through': hasDiscountPrice }">${{ formattedPrice }}</span>
						<span v-if="hasDiscountPrice" class="ml-2">${{ discountPrice }}</span>
					</div>
				</div>
				<div v-if="filteredOptionSelections?.length" class="mt-2">
					<div v-for="option in filteredOptionSelections" :key="option.id" class="text-xs text-ihh-midnight-gray">
						<span class="font-semibold">{{ `${option.option_name}: ` }}</span>
						<span class="text-ihh-gray">{{ option.option_value }}</span>
					</div>
				</div>
				<div v-if="item.variant" class="text-ihh-midnight-gray font-semibold pt-2 px-2 text-xs">
					<div v-for="option in item.variant.optionValues" :key="option.label">
						<span>{{ `${option.optionDisplayName}: ` }}</span>
						<span class="text-ihh-gray font-normal">{{ option.label }}</span>
					</div>
					<div v-if="XCoverProducts.includes(item.item?.bcId)">
						<span>{{ `Covered Product Code: ` }}</span>
						<span class="text-ihh-gray font-normal">{{ item.optional?.coveredProductCode }}</span>
					</div>
					<div v-if="XCoverProducts.includes(item.item?.bcId)">
						<span>{{ `Covered Product: ` }}</span>
						<span class="text-ihh-gray font-normal">{{ item.optional.productName }}</span>
					</div>
				</div>
				<div v-if="bulkPricingActive" class="text-xs mt-2">
					<span class="bg-ihh-check rounded py-1 px-2 text-ihh-midnight-gray font-ihh-bold">Bulk Pricing Unlocked!</span>
				</div>
			</div>
		</div>

		<div class="flex w-full justify-between text-sm text-ihh-midnight-gray">
			<div class="flex w-28 justify-around max-w-[100px]">
				<button
					@click="emitReduceQty"
					class="shadow shadow-ihh-midnight-gray/20 m-auto flex w-5 h-5 rounded-full drop-shadow-2xl shadow-black bg-white items-center justify-center"
				>
					-
				</button>
				<div class="m-auto text-sm">{{ item.quantity }}</div>
				<button
					@click="emitIncreaseQty"
					class="shadow shadow-ihh-midnight-gray/20 m-auto flex w-5 h-5 rounded-full drop-shadow-2xl shadow-black bg-white items-center justify-center"
				>
					+
				</button>
			</div>
			<div>
				<button @click="emitRemoveItem" class="underline text-xs hover:text-ihh-dark-salmon group">Remove</button>
			</div>
		</div>
	</div>
</template>

<script>
import Swal from 'sweetalert2'

export default {
	name: 'BagItem',
	props: {
		item: {
			type: Object,
			required: true,
			validator: (item) => 'item' in item && 'quantity' in item,
		},
		XCoverProducts: {
			type: Array,
			default: () => [],
		},
	},
	methods: {
		handleProductClick() {
			this.$emit('product-click', this.item.item)
		},
		emitReduceQty() {
			this.$emit('reduce-qty', this.item.variant?.id || this.item.item.id)
		},
		emitIncreaseQty() {
			if (this.item?.quantity + this.purchaseIncrement <= this.currentStock) {
				this.$emit('increase-qty', this.item.variant?.id || this.item.item.id)
			} else {
				Swal.fire({
					icon: 'warning',
					title: `Only ${this.currentStock} left in stock!`,
					text: `"${this.item?.item?.name}" has low stock.`,
					confirmButtonColor: '#e59789',
				})
			}
		},
		emitRemoveItem() {
			this.$emit('remove-item', this.item.variant?.id || this.item.item.id)
		},
	},
	computed: {
		currentPrice() {
			// If no variant or quantity is invalid, return the regular price
			if (!this.item.variant?.currentPrice || !this.item.quantity || this.item.quantity <= 0) {
				return this.item.variant?.currentPrice || this.item.item?.currentPrice
			}

			// If no bulk pricing rules available, return the regular price
			if (!this.item.item?.bulkPricingRules || this.item.item.bulkPricingRules.length === 0) {
				return this.item.variant?.currentPrice || this.item.item?.currentPrice
			}

			// Find the applicable bulk pricing rule based on quantity
			const applicableRule = this.item.item.bulkPricingRules
				.filter((rule) => this.item.quantity >= rule.quantityMin && (rule.quantityMax === 0 || this.item.quantity <= rule.quantityMax))
				.sort((a, b) => b.quantityMin - a.quantityMin)[0] // Get the rule with the highest min quantity that applies

			// If no applicable rule found, return the regular price
			if (!applicableRule) {
				return this.item.variant?.currentPrice || this.item.item?.currentPrice
			}

			// Base price to apply discount to
			const basePrice = this.item.variant?.currentPrice || this.item.item?.currentPrice

			// Calculate price based on the rule type
			switch (applicableRule.type) {
				case 'price':
					// Price adjustment per product (subtract the amount from the original price)
					return basePrice - applicableRule.amount
				case 'percent':
					// Percentage discount (apply percentage reduction to original price)
					return basePrice * (1 - applicableRule.amount / 100)
				case 'fixed':
					// Fixed price regardless of original price
					return applicableRule.amount
				default:
					return basePrice
			}
		},
		formattedPrice() {
			if (!this.item?.price) return this.currentPrice?.toFixed(2)
			return this.item.price.originalPrice.toFixed(2)
		},
		discountPrice() {
			return this.item?.price?.salePrice.toFixed(2)
		},
		hasDiscountPrice() {
			// Return true IF:
			// 1. The original price is not equal to the sale price
			// 2. The discount price is not null
			// 3. The original price is not null
			if (!this.item?.price) return false
			return this.item?.price?.originalPrice !== this.item?.price?.salePrice && !!this.discountPrice && !!this.item?.price?.originalPrice
		},
		bulkPricingActive() {
			return (
				this.item.item?.bulkPricingRules?.length > 0 &&
				!!this.item.item.bulkPricingRules
					.filter((rule) => this.item.quantity >= rule.quantityMin && (rule.quantityMax === 0 || this.item.quantity <= rule.quantityMax))
					.sort((a, b) => b.quantityMin - a.quantityMin)[0]
			)
		},
		currentStock() {
			const product = this.item?.item
			const variant = this.item?.variant
			if (product?.inventoryTracking == 'none') return 500
			if (product?.inventoryTracking == 'variant') {
				return variant?.inventoryLevel
			} else if (product?.inventoryTracking == 'product') {
				return product?.inventoryLevel
			}
		},
		purchaseIncrement() {
			// Required purchase increment: the variant's wins, falling back to the product's, then 1.
			const variantInc = Number(this.item?.variant?.purchaseIncrement)
			if (Number.isFinite(variantInc) && variantInc > 0) return variantInc
			const productInc = Number(this.item?.item?.purchaseIncrement)
			if (Number.isFinite(productInc) && productInc > 0) return productInc
			return 1
		},
		stockAvailable() {
			if (this.currentStock > this.item?.quantity) {
				return true
			} else {
				return false
			}
		},
		filteredOptionSelections() {
			if (!this.item?.optionSelections?.length) return []
			return this.item.optionSelections.filter((option) => {
				const optionName = option.option_name?.toLowerCase() || ''
				return !optionName.includes('file')
			})
		},
	},
}
</script>
